import React from "react";
import { Link } from "react-router-dom";
import { LOGO_URL, buildWhatsAppLink, EMAIL, PHONE_DISPLAY, WHATSAPP_NUMBER } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";
import { openCookieSettings } from "./CookieBanner";

export const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative bg-black text-white overflow-hidden">
      {/* AW watermark */}
      <div
        aria-hidden
        className="absolute -right-20 -bottom-20 w-[420px] h-[420px] opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `url(${LOGO_URL})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          filter: "invert(1)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white flex items-center justify-center">
                <img src={LOGO_URL} alt="AW" className="w-10 h-10 object-contain" />
              </div>
              <div>
                <div className="font-serif-aw text-2xl">Alpha Watch</div>
                <div className="font-sans-aw text-[10px] tracking-luxury uppercase text-white/60 mt-1">
                  Kristian Y. · Zürich
                </div>
              </div>
            </div>
            <p className="font-sans-aw text-sm md:text-base text-white/60 mt-8 max-w-md leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Nav */}
          <div className="md:col-span-3">
            <h4 className="font-sans-aw text-[10px] tracking-luxury uppercase text-white/40 mb-6">
              {t.footer.nav_title}
            </h4>
            <ul className="flex flex-col gap-3 font-sans-aw text-sm">
              <li>
                <Link data-testid="footer-link-home" to="/" className="text-white/80 hover:text-white link-aw">
                  {t.nav.home}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-link-about" to="/about" className="text-white/80 hover:text-white link-aw">
                  {t.nav.about}
                </Link>
              </li>
              <li>
                <Link data-testid="footer-link-services" to="/services" className="text-white/80 hover:text-white link-aw">
                  {t.nav.services}
                </Link>
              </li>
              <li>
                <a data-testid="footer-link-gallery" href="https://www.chrono24.ch/search/index.htm?customerId=36784&dosearch=true" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white link-aw">
                  {t.nav.catalog}
                </a>
              </li>
              <li>
                <Link data-testid="footer-link-contact" to="/contact" className="text-white/80 hover:text-white link-aw">
                  {t.nav.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="font-sans-aw text-[10px] tracking-luxury uppercase text-white/40 mb-6">
              {t.footer.contact_title}
            </h4>
            <address className="not-italic font-sans-aw text-sm text-white/80 leading-relaxed">
              {t.contact.address_lines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </address>
            <div className="mt-5 space-y-2 font-sans-aw text-sm">
              <a data-testid="footer-phone" href={`tel:+${WHATSAPP_NUMBER}`} className="block text-white/80 hover:text-white">
                {PHONE_DISPLAY}
              </a>
              <a data-testid="footer-email" href={`mailto:${EMAIL}`} className="block text-white/80 hover:text-white break-all">
                {EMAIL}
              </a>
            </div>
            <a
              data-testid="footer-whatsapp-link"
              href={buildWhatsAppLink(t.whatsapp.default_message)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-6 font-sans-aw text-sm text-white link-aw"
            >
              {t.contact.whatsapp_cta}
            </a>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="font-sans-aw text-[11px] tracking-luxury-md uppercase text-white/40">
            © {year} Alpha Watch Kristian Y. · {t.footer.rights}
          </p>
          <div className="flex gap-6 font-sans-aw text-[11px] tracking-luxury-md uppercase text-white/40">
            <a data-testid="footer-imprint" href="/impressum" className="hover:text-white">{t.footer.imprint}</a>
            <a data-testid="footer-privacy" href="/datenschutz" className="hover:text-white">{t.footer.privacy}</a>
            <button data-testid="footer-cookie-settings" type="button" onClick={openCookieSettings} className="uppercase tracking-luxury-md hover:text-white">{t.footer.cookies}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};
